import { useCallback, useRef, useState } from 'react'
import { Turnstile } from '../components/Turnstile'
import { ENV } from './env'

/**
 * Captcha for an auth form. Renders the Turnstile widget and, crucially, lets the submit handler WAIT for a
 * token instead of firing without one: on a slow connection the managed challenge takes a second or two, and
 * a form that read the token synchronously would send an empty one and be rejected ("no captcha_token
 * found"). waitForToken() resolves immediately if a token is already in hand, resolves '' at once when no
 * site key is configured (captcha off in the build), and otherwise waits up to `ms` for the widget, then
 * resolves with whatever it has (so a genuinely broken widget still lets a captcha-disabled server through
 * rather than hanging forever). The token is single-use; call reset() after a failed attempt for a fresh one.
 */
export function useCaptcha(opts?: { appearance?: 'always' | 'interaction-only'; labelled?: boolean }) {
  const [token, setToken] = useState('')
  const tokenRef = useRef('')
  const waiters = useRef<Array<(t: string) => void>>([])
  const resetRef = useRef<(() => void) | null>(null)

  const onToken = useCallback((t: string) => {
    tokenRef.current = t
    setToken(t)
    if (t) {
      const pending = waiters.current
      waiters.current = []
      pending.forEach((w) => w(t))
    }
  }, [])

  const waitForToken = useCallback((ms = 20000): Promise<string> => {
    if (tokenRef.current) return Promise.resolve(tokenRef.current)
    if (!ENV.TURNSTILE_SITE_KEY) return Promise.resolve('')
    return new Promise<string>((resolve) => {
      const done = (t: string) => resolve(t)
      waiters.current.push(done)
      setTimeout(() => {
        waiters.current = waiters.current.filter((w) => w !== done)
        resolve(tokenRef.current || '')
      }, ms)
    })
  }, [])

  const reset = useCallback(() => {
    tokenRef.current = ''
    setToken('')
    resetRef.current?.()
  }, [])

  const widget = (
    <Turnstile
      onToken={onToken}
      onReset={(fn) => (resetRef.current = fn)}
      labelled={opts?.labelled ?? true}
      appearance={opts?.appearance}
    />
  )
  return { token, widget, waitForToken, reset, hasWidget: !!ENV.TURNSTILE_SITE_KEY }
}
